import { useRef, useState } from "react"
import { FileText, X, Loader2 } from "lucide-react"
import { Button } from "@/modules/shared/ui/components/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/modules/shared/ui/components/dialog"
import EmployeeForm, {
  EmployeeFormValues,
} from "@/modules/onboarding/ui/components/EmployeeForm"
import { FileCategory } from "@/modules/shared/hooks/use-file-validation"
import { DraggableFileUpload, DraggableFileUploadRef } from "@/modules/shared/ui/components/custom/file-upload"

interface AcceptCandidateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (file: File, employeeData: EmployeeFormValues) => Promise<void>
  candidateName: string
  candidateData?: Partial<EmployeeFormValues>
  isUploading: boolean
  formId: string
  availableJobs?: {
    id: string
    title: string
  }[]
}

export function AcceptCandidateDialog({
  open,
  onOpenChange,
  onConfirm,
  candidateName,
  candidateData,
  isUploading,
  formId,
  availableJobs,
}: AcceptCandidateDialogProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileUploadRef = useRef<DraggableFileUploadRef>(null);

  const handleSubmit = async (employeeData: EmployeeFormValues) => {
    if (selectedFile) {
      await onConfirm(selectedFile, employeeData)
      setSelectedFile(null)
      fileUploadRef.current?.clearError()
    }
  }

  const handleCancel = () => {
    setSelectedFile(null)
    fileUploadRef.current?.clearError()
    onOpenChange(false)
  }

  const readOnlyFields = candidateData
    ? Object.keys(candidateData).filter((key) => key !== "documentsNeeded")
    : []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-full max-w-3xl p-0 max-h-[90vh] overflow-y-auto bg-card"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="p-6 border-b border-border">
          <DialogHeader className="gap-1">
            <DialogTitle className="text-2xl font-bold">
              Accept Candidate
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Upload the contract for {candidateName}.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <h3 className="text-base font-semibold text-foreground mb-3">
              Upload Offer
            </h3>
            {!selectedFile ? (
              <DraggableFileUpload ref={fileUploadRef} onFileSelected={setSelectedFile} fileCategory={FileCategory.Document} />
            ) : (
              <div className="border border-border rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="h-8 w-8 text-primary" />
                  <div>
                    <p className="font-medium text-foreground">
                      {selectedFile.name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedFile(null)
                    fileUploadRef.current?.clearError()
                  }}
                  disabled={isUploading}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          <div>
            <h3 className="text-base font-semibold text-foreground mb-3">
              Employee Details
            </h3>
            <div className="relative">
              <EmployeeForm
                key={open ? JSON.stringify(candidateData) : "closed"}
                formId={formId}
                showActions={false}
                onSubmit={handleSubmit}
                availableJobs={availableJobs}
                defaultValues={candidateData}
                readOnlyFields={readOnlyFields}
              />
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-4">
          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              id="acceptCandidate"
              type="submit"
              form={formId}
              disabled={!selectedFile || isUploading}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {isUploading ?
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </> : "Accept & Invite Candidate"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
